<template>
  <AnimatePresence
    v-if="isAnimated"
    mode="sync"
    :custom="transitionDirection"
    :initial="false"
  >
    <motion.div
      v-if="isActive"
      :key="name"
      class="panels--frame"
      role="tabpanel"
      :custom="transitionDirection"
      :variants="transitionVariants"
      initial="initial"
      animate="animate"
      exit="exit"
      :transition="transitionOptions"
    >
      <slot />
    </motion.div>
  </AnimatePresence>

  <div
    v-else
    class="panels--frame"
    role="tabpanel"
  >
    <slot />
  </div>
</template>

<script setup>
import { inject, computed, onMounted, onBeforeUnmount } from 'vue'
import { AnimatePresence, motion } from 'motion-v'

defineOptions({ name: 'RoutePanel' })

const props = defineProps({
  name: { type: String, required: true },
  disable: { type: Boolean, default: false },
  to: { type: [String, Object], required: true },
  exact: { type: Boolean, default: false },
  replace: { type: Boolean, default: false }
})

const $panels = inject('panels', null)

const isAnimated = computed(() => $panels?.animated.value)
const isActive = computed(() => $panels?.active.value === props.name && !props.disable)
const isVertical = computed(() => $panels?.vertical.value)
const transitionDirection = computed(() => {
  const myIdx = $panels?.panels.value.find(p => p.name === props.name)?.idx ?? null
  const prevIdx = $panels?.panels.value.find(p => p.name === $panels?.prevActive.value)?.idx ?? null
  const activeIdx = $panels?.panels.value.find(p => p.name === $panels?.active.value)?.idx ?? null

  if (myIdx === null || activeIdx === null) return null

  let dir = null
  if (myIdx === prevIdx) dir = myIdx > activeIdx ? 1 : -1
  else if (myIdx === activeIdx) dir = myIdx > prevIdx ? 1 : -1

  return dir
})
const transitionVariants = computed(() => ({
  initial: dir => {
    const offset = `${0.3 * dir * 100}%`
    return {
      opacity: 0,
      visibility: 'hidden',
      ...(isVertical.value ? { y: offset } : { x: offset })
    }
  },
  animate: {
    opacity: 1,
    visibility: 'visible',
    ...(isVertical.value ? { y: 0 } : { x: 0 })
  },
  exit: dir => {
    const offset = `${0.3 * dir * 100}%`
    return {
      opacity: 0,
      visibility: 'hidden',
      ...(isVertical.value ? { y: offset } : { x: offset })
    }
  }
}))
const transitionOptions = computed(() => ({
  duration: ($panels?.transitionDuration.value ?? 300) / 1000,
  ease: [0.4, 0, 0.2, 1]
}))

onMounted(() => {
  $panels?.register(props.name, () => props.disable)
  $panels?.registerRoutePanel({ name: props.name, to: props.to, exact: props.exact })
})

onBeforeUnmount(() => {
  $panels?.unregister(props.name)
  $panels?.unregisterRoutePanel(props.name)
})
</script>

<style scoped>
@reference "#app-styles.css";

.panels--frame {
  grid-area: 1 / 1;
  @apply w-full;
}
</style>
